import { Button, ButtonGroup } from '@chakra-ui/button';
import { Heading } from '@chakra-ui/layout';
import { useToast } from '@chakra-ui/toast';
import React, { useEffect, useMemo, useState } from 'react';
import Layout from '@/components/Layout';
import ToggleRow from '@/components/ToggleRow';
import SKCard from '@/components/UI/SKCard';
import { SIKKA_FAQS, SECTION_NAMES } from '@/data/faq';
import classes from './styles.module.scss';
import LinkIcon from '@/public/assets/icons/link.svg';
import { FAQS_PAGE } from '@/constants/route.const';
import { copyToClickBoard } from '@/utils';
import { PACKAGE } from '@/constants';
import { StorageService } from '@/service/Storage';
import { useRouter } from 'next/router';

const SUPPORTED_LANGS = ['en', 'hi', 'mr', 'gu', 'id'];

export default function Faq() {
  const router = useRouter();
  const toast = useToast();
  
  const getLangFromQuery = () => {
    const queryLang = router.query.lang;
    // Map 'in' to 'id' for Indonesian
    const mappedLang = queryLang === 'in' ? 'id' : queryLang;
    // Validate against supported languages
    return SUPPORTED_LANGS.includes(mappedLang) ? mappedLang : 'en';
  };

  const [lang, setLang] = useState('en');

  useEffect(() => {
    const initialLang = getLangFromQuery();
    setLang(initialLang);
    StorageService.setXPN(PACKAGE);
  }, [router.query.lang]);

  // Group FAQs by section
  const groupedFaqs = useMemo(() => {
    const groups = {};
    SIKKA_FAQS.forEach(faq => {
      const sectionKey = faq.sectionKey;
      if (!groups[sectionKey]) {
        groups[sectionKey] = [];
      }
      groups[sectionKey].push(faq);
    });
    return groups;
  }, []);

  const pageTitle = {
    en: 'Frequently Asked Questions',
    hi: 'अक्सर पूछे जाने वाले प्रश्न',
    mr: 'वारंवार विचारले जाणारे प्रश्न',
    gu: 'વારંવાર પૂછાતા પ્રશ્નો',
    id: 'Pertanyaan yang Sering Diajukan'
  };

  return (
    <Layout>
      <div className={classes.FaqPage}>
        <Heading as="h1">{pageTitle[lang]}</Heading>
        
        <div style={{ margin: '1rem 0rem' }}>
          <ButtonGroup variant="solid" spacing="0" size="xs" isAttached>
            <Button
              bg={lang === 'en' ? '#8056B2' : 'gray.300'}
              color={lang === 'en' ? 'white' : 'gray.700'}
              _hover={{ bg: lang === 'en' ? '#6d4a96' : 'gray.400' }}
              onClick={() => setLang('en')}
            >
              English
            </Button>
            <Button
              bg={lang === 'hi' ? '#8056B2' : 'gray.300'}
              color={lang === 'hi' ? 'white' : 'gray.700'}
              _hover={{ bg: lang === 'hi' ? '#6d4a96' : 'gray.400' }}
              onClick={() => setLang('hi')}
            >
              हिंदी
            </Button>
            <Button
              bg={lang === 'mr' ? '#8056B2' : 'gray.300'}
              color={lang === 'mr' ? 'white' : 'gray.700'}
              _hover={{ bg: lang === 'mr' ? '#6d4a96' : 'gray.400' }}
              onClick={() => setLang('mr')}
            >
              मराठी
            </Button>
            <Button
              bg={lang === 'gu' ? '#8056B2' : 'gray.300'}
              color={lang === 'gu' ? 'white' : 'gray.700'}
              _hover={{ bg: lang === 'gu' ? '#6d4a96' : 'gray.400' }}
              onClick={() => setLang('gu')}
            >
              ગુજરાતી
            </Button>
            <Button
              bg={lang === 'id' ? '#8056B2' : 'gray.300'}
              color={lang === 'id' ? 'white' : 'gray.700'}
              _hover={{ bg: lang === 'id' ? '#6d4a96' : 'gray.400' }}
              onClick={() => setLang('id')}
            >
              Indonesia
            </Button>
          </ButtonGroup>
        </div>

        <div className={classes.FaqList}>
          {Object.keys(groupedFaqs).map(sectionKey => (
            <div key={sectionKey}>
              <h2 className={classes.sectionTitle}>
                {SECTION_NAMES[sectionKey][lang]}
              </h2>
              {groupedFaqs[sectionKey].map(faq => (
                <SKCard className={classes.card} id={`${faq.key}`} key={faq.key}>
                  <ToggleRow
                    key={faq.key}
                    header={
                      <div className={classes.rowHeader}>
                        <LinkIcon
                          className={classes.linkIcon}
                          onClick={() => {
                            const FAQ_LINK = `${window.location.origin}${FAQS_PAGE}?lang=${lang === 'id' ? 'in' : lang}#${faq.key}`;
                            router.replace(`${FAQS_PAGE}#${faq.key}`);
                            if (copyToClickBoard(FAQ_LINK)) {
                              toast({ title: 'Link copied!', key: 'faq-link', status: 'success', duration: 2000 });
                            }
                          }}
                        />
                        {faq[lang].title}
                      </div>
                    }
                    body={<div>{faq[lang].desc}</div>}
                    defaultOpen={`#${faq.key}` === `#${router.asPath.split('#')[1]}`}
                  />
                </SKCard>
              ))}
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
